import { Extension, InputRule, nodeInputRule, type InputRuleMatch } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

type MarkdownImageShortcutData = {
  alt: string;
  src: string;
};

type MarkdownLinkShortcutData = {
  href: string;
  label: string;
};

const isRemoteHttpUrl = (value: string) => {
  try {
    const url = new URL(value);

    return (url.protocol === "http:" || url.protocol === "https:") && Boolean(url.hostname);
  } catch {
    return false;
  }
};

const MARKDOWN_PASTE_PATTERNS = [
  /^#{1,6}\s+\S/m,
  /^\s{0,3}>\s+\S/m,
  /^\s*[-+*]\s+(?:\[[ xX]\]\s+)?\S/m,
  /^\s*\d+[.)]\s+\S/m,
  /^\s*```[^\n]*$/m,
  /^\s{0,3}(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,})$/m,
  /^\s*\|?.+\|.+\n\s*\|?\s*:?-{3,}/m,
  /!\[[^\]\n]*\]\([^\s)]+(?:\s+["'][^"']*["'])?\)/,
  /\[[^\]\n]+\]\([^\s)]+(?:\s+["'][^"']*["'])?\)/,
  /(?:\*\*|__)[^\n]+(?:\*\*|__)/,
  /~~[^\n]+~~/,
];

export const isMarkdownPaste = (text: string) => {
  const normalizedText = text.replace(/\r\n?/g, "\n").trim();

  return normalizedText.length > 0 && MARKDOWN_PASTE_PATTERNS.some((pattern) => pattern.test(normalizedText));
};

export const findMarkdownImageShortcut = (text: string): InputRuleMatch | null => {
  const match = /^!\[([^\]\n]*)\]\((https?:\/\/[^\s]+)\)$/.exec(text);

  if (!match || !isRemoteHttpUrl(match[2])) {
    return null;
  }

  return {
    text: match[0],
    index: 0,
    data: {
      alt: match[1],
      src: match[2],
    } satisfies MarkdownImageShortcutData,
  };
};

export const findMarkdownLinkShortcut = (text: string): InputRuleMatch | null => {
  const match = /\[([^\]\n]+)\]\((https?:\/\/[^\s]+)\)$/.exec(text);

  if (!match || match.index == null || text[match.index - 1] === "!" || !isRemoteHttpUrl(match[2])) {
    return null;
  }

  return {
    text: match[0],
    index: match.index,
    data: {
      href: match[2],
      label: match[1],
    } satisfies MarkdownLinkShortcutData,
  };
};

export const MarkdownShortcuts = Extension.create({
  name: "markdownShortcuts",

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            if (event.clipboardData?.files.length) {
              return false;
            }

            if (event.clipboardData?.getData("text/html").trim()) {
              return false;
            }

            const text = event.clipboardData?.getData("text/plain") ?? "";

            if (
              view.state.selection.$from.parent.type.name === "codeBlock" ||
              !isMarkdownPaste(text)
            ) {
              return false;
            }

            const inserted = editor.commands.insertContent(text.replace(/\r\n?/g, "\n"), {
              contentType: "markdown",
            });

            if (!inserted) {
              return false;
            }

            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },

  addInputRules() {
    const imageType = this.editor.schema.nodes.image;
    const linkType = this.editor.schema.marks.link;

    if (!imageType || !linkType) {
      return [];
    }

    return [
      nodeInputRule({
        find: findMarkdownImageShortcut,
        type: imageType,
        getAttributes: (match) => {
          const data = match.data as MarkdownImageShortcutData;

          return {
            alt: data.alt || null,
            src: data.src,
          };
        },
      }),
      new InputRule({
        find: findMarkdownLinkShortcut,
        handler: ({ match, range, state }) => {
          const data = match.data as MarkdownLinkShortcutData | undefined;

          if (!data) {
            return null;
          }

          const existingMarks = state.doc
            .resolve(range.from)
            .marks()
            .filter((mark) => mark.type !== linkType);
          const linkMark = linkType.create({ href: data.href });

          state.tr
            .replaceWith(range.from, range.to, state.schema.text(data.label, [...existingMarks, linkMark]))
            .removeStoredMark(linkType);
        },
      }),
    ];
  },
});
